import {
  isAfter as _isAfter,
  isBefore as _isBefore,
  addDays as _addDays,
  addSeconds as _addSeconds,
  addMinutes as _addMinutes,
  addHours as _addHours,
  addYears as _addYears,
  isSameSecond as _isSameSecond,
  isSameDay as _isSameDay,
  isWithinRange as _isWithinRange,
  differenceInDays as _differenceInDays,
  differenceInMilliseconds as _differenceInMilliseconds,
  endOfDay as _endOfDay,
  format as _format,
  distanceInWordsStrict as _distanceInWordsStrict,
  getTime as _getTime,
  getMinutes as _getMinutes,
  getHours as _getHours,
  getDayOfYear as _getDayOfYear,
  parse as _parse,
  startOfHour as _startOfHour,
  startOfDay as _startOfDay,
  setMinutes as _setMinutes,
  setHours as _setHours,
  subMinutes as _subMinutes,
  subHours as _subHours,
} from 'date-fns';

export const MILLISECONDS = 1000;
export const TIMEZONE_LA = 'America/Los_Angeles';
export const FORMAT_DISTANCE = 'FORMAT_DISTANCE';
export const FORMAT_ISO = 'ISO';
export const FORMAT_UNIX = 'UNIX';
export const FORMAT_UNIX_MS = 'UNIX_MS';
export const FORMAT_ISO_8601 = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
export const FORMAT_ISO_8601_WITHOUT_MS = 'YYYY-MM-DDTHH:mm:ssZ';
export const FORMAT_TIME = 'h:mm A';
export const FORMAT_HOUR = 'hA';
export const FORMAT_YEAR_MONTH_DAY = 'YYYY-MM-DD';
export const FORMAT_YEAR_MONTH_DAY_SHORT = 'YY/M/D';
export const FORMAT_WEEKDAY_MONTH_DAY = 'ddd, MMM D';
export const FORMAT_MONTH_DAY = 'MMM D';
export const FORMAT_MONTH_DAY_LONG = 'MMMM D';
export const FORMAT_DAY_OF_WEEK = 'dddd';
export const FORMAT_MINUTES_SECONDS = 'mm:ss';
export const FORMAT_MONTH_DAY_YEAR = 'M-D-YY';
export const FORMAT_MONTH_SLASH_DAY = 'M/D';

export const now = () => new Date();

export const midNightTonightTime = new Date(new Date().setHours(23, 59, 59)).getTime();

export const SEVEN_PM = '19:00:00';
const EIGHT_30_PM = '20:30:00';

export const msToDate = msValue => {
  const date = new Date(msValue);
  if (!isDate(date)) {
    console.log(`DatetimeHelper.msToDate not able to generate a date.`);
    return null;
  }
  return date;
};

const isDate = value => value instanceof Date;

export const stringToDate = value => {
  const name = 'DateTimeHelper.stringToDate';
  if (!value) return null;
  if (isDate(value)) return value;

  // eslint-disable-next-line no-underscore-dangle
  if (value._isAMomentObject) {
    console.log(`${name} moment object sent.`);
    return value.toDate();
  }

  if (typeof value !== 'string') {
    const invalidType = `${name} value is not a string or a date, it's a ${typeof value}`;
    console.log(invalidType);
  }

  const date = parse(value);
  if (!isDate(date)) {
    console.log(`${name} not able to generate a date.`);
    return null;
  }

  return date;
};

export const addDays = (date, days) => _addDays(date, days);
export const addSeconds = (date, seconds) => _addSeconds(date, seconds);
export const addMinutes = (date, minutes) => _addMinutes(date, minutes);
export const addHours = (date, hours) => _addHours(date, hours);
export const addYears = (date, years) => _addYears(date, years);

export const isSameSecond = (dateOne, dateTwo) => _isSameSecond(dateOne, dateTwo);
export const isSameDay = (dateOne, dateTwo) => _isSameDay(dateOne, dateTwo);
export const isBefore = (dateOne, dateTwo) => _isBefore(dateOne, dateTwo);
export const isAfter = (dateOne, dateTwo) => _isAfter(dateOne, dateTwo);

export const isBetween = (date, start, end) => start < end && _isWithinRange(date, start, end);

export const differenceInDays = (dateOne, dateTwo) => _differenceInDays(dateOne, dateTwo);

export const differenceInMilliseconds = (dateOne, dateTwo) =>
  _differenceInMilliseconds(dateOne, dateTwo);

export const differenceInMinutes = (dateOne, dateTwo) => {
  // note: we're not using date-fns's differenceInMinutes.
  // to get the same result as moment's.toMinutes(), we need to get the ms and manually calculate it.
  const diff = differenceInMilliseconds(dateOne, dateTwo);
  return Math.ceil(diff / 60 / 1000);
};

export const differenceInSeconds = (dateOne, dateTwo) => {
  // note: we're not using date-fns's differenceInSeconds.
  // to get the same result as moment's.toMinutes(), we need to get the ms and manually calculate it.
  const diff = differenceInMilliseconds(dateOne, dateTwo);
  return Math.ceil(diff / 1000);
};

export const endOfDay = date => _endOfDay(date);

export const getTimestamp = date => _getTime(date);
export const getDayOfYear = date => _getDayOfYear(date);
export const getMinutes = date => _getMinutes(date);
export const getHours = date => _getHours(date);

export const guessTZ = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const parse = (date, format) => _parse(date, format);

export const parseTime = time => {
  if (!time || typeof time !== 'string' || time.indexOf(':') === -1) return 0;

  const [h, m, s] = time.split(':');
  const hours = h ? parseInt(h, 10) : 0;
  const minutes = m ? parseInt(m, 10) : 0;
  const seconds = s ? parseInt(s, 10) : 0;

  return { hours, minutes, seconds };
};

export const parseTimeToSeconds = time => {
  const { hours, minutes, seconds } = parseTime(time);
  return hours * 60 * 60 + minutes * 60 + seconds;
};
export const parseTimeToHours = time => parseTimeToSeconds(time) / 60 / 60;

export const startOfHour = date => _startOfHour(date);
export const startOfDay = date => _startOfDay(date);

export const setMinutes = (date, minutes) => _setMinutes(date, minutes);
export const setHours = (date, hours) => _setHours(date, hours);

export const setTime = (date, time) => {
  const year = date.getFullYear();
  const month = formatToTwoDigitsString(date.getMonth() + 1);
  const day = formatToTwoDigitsString(date.getDate());

  const { hours, minutes, seconds } = parseTime(time);
  const h = formatToTwoDigitsString(hours);
  const m = formatToTwoDigitsString(minutes);
  const s = formatToTwoDigitsString(seconds);

  const dateString = `${year}-${month}-${day}T${h}:${m}:${s}Z`;

  const result = new Date(dateString);
  return result;
};

export const setDateAndTime = (date, time) => {
  const year = date.getFullYear();
  const month = formatToTwoDigitsString(date.getMonth() + 1);
  const day = formatToTwoDigitsString(date.getDate());
  const dateString = `${month}/${day}/${year} ${time}`;

  return new Date(dateString);
};

export const getMonthAndDay = date => format(date, FORMAT_MONTH_DAY);

export const tonight7PMTime = selectedDate =>
  new Date(`${setDateAndTime(selectedDate, SEVEN_PM)}`).getTime();

export const tonight830PMTime = selectedDate =>
  new Date(`${setDateAndTime(selectedDate, EIGHT_30_PM)}`).getTime();

const calculateTimeDistance = date => {
  const dateDiff = _distanceInWordsStrict(new Date(), date, { addSuffix: true });

  const days =
    dateDiff.includes('day') && parseInt(dateDiff.match(/[1-9]|[12]\d|3[01]/g).join(''), 10);

  if (dateDiff.includes('month') || dateDiff.includes('year') || (days && days > 7)) {
    return format(date, 'M/D/YY');
  }

  const mapObj = {
    seconds: 's',
    second: 's',
    minute: 'm',
    minutes: 'm',
    day: 'd',
    days: 'd',
    hours: 'h',
    hour: 'h',
  };
  const diff = dateDiff.replace(
    /seconds|second|minutes|minute|hours|hour|days|day|month|year/gi,
    matched => mapObj[matched],
  );
  return diff.replace(/\s/, '');
};

export const subMinutes = (date, minutes) => _subMinutes(date, minutes);
export const subHours = (date, hours) => _subHours(date, hours);

export const format = (date, format) => {
  if (format === FORMAT_ISO) return date.toISOString();
  if (format === FORMAT_UNIX) return Math.floor(date.getTime() / 1000);
  if (format === FORMAT_UNIX_MS) return date.getTime();
  if (format === FORMAT_DISTANCE) return calculateTimeDistance(date);
  return _format(date, format);
};

export const secondsToHoursMinutesSeconds = durationInSeconds => {
  const hours = Math.floor(Number(durationInSeconds) / 3600);
  const minutes = Math.floor((Number(durationInSeconds) % 3600) / 60);
  const seconds = Math.floor((Number(durationInSeconds) % 3600) % 60);
  return { hours, minutes, seconds };
};

// https://philna.sh/blog/2021/02/22/display-dates-in-your-users-time-zone/
export const getTimeZoneAbbr = () => {
  const { locale, timeZone } = Intl.DateTimeFormat().resolvedOptions();
  return Intl.DateTimeFormat(locale, { timeZoneName: 'short', timeZone })
    .formatToParts()
    .find(part => part.type === 'timeZoneName')
    .value.replace(/[^a-zA-Z]+/g, '');
};

export const formatToAPIRange = (dateOne, dateTwo) => {
  const start = format(dateOne, FORMAT_ISO);
  const end = format(dateTwo, FORMAT_ISO);
  const range = `${start}..${end}`;
  return range;
};
export const formatToTwoDigitsString = string => `0${string}`.substr(-2);
export const formatToTwoDigitsTimezone = tz => {
  // this is to format the 3 or 4 digit abbreviation into
  // 2 or 3 digits as we want to display it.
  // http://www.timetemperature.com/abbreviations/united_states_time_zone_abbreviations.shtml
  const abbrs = {
    AST: 'AT', // ATLANTIC STANDARD TIME
    EST: 'ET', // EASTERN STANDARD TIME
    EDT: 'ET', // EASTERN DAYLIGHT TIME
    CST: 'CT', // CENTRAL STANDARD TIME
    CDT: 'CT', // CENTRAL DAYLIGHT TIME
    MST: 'MT', // MOUNTAIN STANDARD TIME
    MDT: 'MT', // MOUNTAIN DAYLIGHT TIME
    PST: 'PT', // PACIFIC STANDARD TIME
    PDT: 'PT', // PACIFIC DAYLIGHT TIME
    AKST: 'AKT', // ALASKA TIME
    AKDT: 'AKT', // ALASKA DAYLIGHT TIME
    HST: 'HT', // HAWAII STANDARD TIME
    HAST: 'HT', // HAWAII-ALEUTIAN STANDARD TIME
    HADT: 'HT', // HAWAII-ALEUTIAN DAYLIGHT TIME
    SST: 'ST', // SAMOA STANDARD TIME
    SDT: 'ST', // SAMOA DAYLIGHT TIME
    CHST: 'CHT', // CHAMORRO STANDARD TIME
  };

  // eslint-disable-next-line no-restricted-globals
  const fallback = !abbrs[tz] && isNaN(tz) ? tz : null;
  return abbrs[tz] || fallback;
};

export const getPrimetimeDateRange = () => {
  const daysToAdd = 7;
  const start = startOfDay(now());
  const end = startOfDay(addDays(now(), daysToAdd));
  return formatToAPIRange(start, end);
};

export const setTimeForDateAtATimezone = (date, time, timezone) => {
  if (!isDate(date)) {
    console.log('DateTimeHelper.setTimeForDateAtATimezone date is not a valid date.');
    return null;
  }

  const offsetInHours = date.getTimezoneOffset() / 60;
  const newDate = setTime(date, time);

  return new Date(newDate.setHours(newDate.getHours() + offsetInHours * -1));
};
